/**
 * Session and onboarding state for the route guards (spec §14.1).
 *
 * `GET /me` is the single source of truth about who is signed in; the browser
 * never inspects the session cookie. The guards live in `beforeLoad`, so a
 * route component can assume it has a user.
 */
import type { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api-client.js";
import type { MadalineApi, Me, OnboardingState } from "./api.js";
import { queryKeys, STALE } from "./query-client.js";

/** §14.1: `/onboarding?step=region|venue|execution|disclosures|first-strategy`. */
export const ONBOARDING_STEPS = [
  "region",
  "venue",
  "execution",
  "disclosures",
  "first-strategy",
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

const STATE_TO_STEP: Record<OnboardingState, OnboardingStep | "done"> = {
  region: "region",
  venue: "venue",
  execution: "execution",
  disclosures: "disclosures",
  first_strategy: "first-strategy",
  done: "done",
};

/**
 * Fetch `/me`, or null when nobody is signed in.
 *
 * A 401 is an answer, not a failure: it is how the guard learns to redirect.
 * Anything else (a 500, the API being down) propagates so the route's error
 * boundary shows it rather than bouncing the user to a login form that will
 * not work either.
 */
export async function ensureMe(
  queryClient: QueryClient,
  api: MadalineApi,
): Promise<Me | null> {
  try {
    return await queryClient.fetchQuery({
      queryKey: queryKeys.me,
      queryFn: () => api.getMe(),
      staleTime: STALE.me,
      retry: false,
    });
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthenticated) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

const ONBOARDED_KEY = "madaline.onboarded";

/**
 * The SERVER decides whether an account is onboarded: `POST /onboarding/complete`
 * sets `user_profiles.onboarding_state` to `done`, and `isOnboarded` reads it.
 *
 * This localStorage record is what stood in for that before the route existed
 * (2026-10-13), and it is kept for one reason only: accounts that finished
 * onboarding under the old build still say `region` on the server, and without
 * this they would be walked through the flow again in the browser they have
 * always used. The flow itself is safe to repeat — its last step is idempotent
 * and re-opens the strategy they already have — so this is a courtesy, not a
 * guard. It can go once no such account is left:
 *
 *   SELECT count(*) FROM user_profiles p
 *    WHERE onboarding_state <> 'done'
 *      AND EXISTS (SELECT 1 FROM strategies s WHERE s.user_id = p.user_id);
 */
export function markOnboarded(userId: string): void {
  try {
    const raw = window.localStorage.getItem(ONBOARDED_KEY);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    if (!ids.includes(userId)) {
      window.localStorage.setItem(ONBOARDED_KEY, JSON.stringify([...ids, userId]));
    }
  } catch {
    // A private window with storage disabled just repeats the venue step.
  }
}

function onboardedLocally(userId: string): boolean {
  try {
    const raw = window.localStorage.getItem(ONBOARDED_KEY);
    return raw ? (JSON.parse(raw) as string[]).includes(userId) : false;
  } catch {
    return false;
  }
}

export function isOnboarded(me: Me): boolean {
  return me.profile.onboarding_state === "done" || onboardedLocally(me.user.id);
}

/** The step the user should resume at. */
export function onboardingStep(me: Me): OnboardingStep {
  const step = STATE_TO_STEP[me.profile.onboarding_state];
  return step === "done" ? "venue" : step;
}
